import BackIcon from '@mui/icons-material/ArrowBack';
import KeepIcon from '@mui/icons-material/Done';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {useTheme} from '@mui/material/styles';
import {useQuery} from '@tanstack/react-query';
import FileSaver from 'file-saver';
import PropTypes from 'prop-types';
import React, {useMemo, useState} from 'react';
import {fetchSeriesInfo} from '../api.js';
import {sanitizeLinks} from '../util/links.js';
import {getVisualizationSeries} from '../util/map.js';
import {ReviewChangeStep} from './ReviewChange.jsx';
import {ReviewStep} from './ReviewObjects.jsx';
import {Modal} from './modal.js';

const STEPS = {
  REVIEW: 0,
  EXPORT: 1,
};

export const ReviewDetections = ({
  open,
  onClose,
  changeDetection,
  detections,
  layer,
  feed,
}) => {
  const [step, setStep] = useState(STEPS.REVIEW);
  const [exportFeatures, setExportFeatures] = useState([]);
  const {data: seriesInfo, isLoading: loading} = useQuery({
    queryKey: ['series', getVisualizationSeries(feed)],
    queryFn: () => fetchSeriesInfo(getVisualizationSeries(feed)),
    enabled: changeDetection,
  });

  const Review = changeDetection === true ? ReviewChangeStep : ReviewStep;

  return (
    <Modal open={open} className="pl-mode-dark">
      {loading && <CircularProgress />}
      {!loading && step === STEPS.REVIEW && (
        <Review
          detections={detections}
          layer={layer}
          feed={feed}
          onExport={detects => {
            setExportFeatures(detects);
            setStep(STEPS.EXPORT);
          }}
          seriesInfo={seriesInfo}
          onClose={() => onClose()}
        />
      )}
      {!loading && step === STEPS.EXPORT && (
        <ExportStep
          features={exportFeatures}
          onBack={() => setStep(STEPS.REVIEW)}
          onClose={() => onClose()}
        />
      )}
    </Modal>
  );
};

ReviewDetections.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
  detections: PropTypes.array,
  layer: PropTypes.string,
  changeDetection: PropTypes.bool,
  feed: PropTypes.object,
};

const ConfirmBadge = () => {
  const theme = useTheme();

  const parentSize = 77;
  const iconSize = 33;

  return (
    <Box
      sx={{
        borderRadius: '50%',
        border: `solid 2px ${theme.vars.palette.success.main}`,
        width: parentSize,
        height: parentSize,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <KeepIcon
        sx={{
          width: iconSize,
          height: iconSize,
          color: theme.vars.palette.success.main,
        }}
      />
    </Box>
  );
};

const sanitize = feature => {
  const properties = {...feature.properties};
  delete properties._include;

  const sanitized = {
    ...feature,
    properties,
  };

  if (feature.links) {
    sanitized.links = sanitizeLinks(feature.links);
  }

  return sanitized;
};

const saveFeatures = (filename, features) => {
  const nextFeatures = [];
  for (let i = 0, ii = features.length; i < ii; i++) {
    if (features[i].properties._include === true) {
      nextFeatures.push(sanitize(features[i]));
    }
  }

  const geojsonString = JSON.stringify({
    type: 'FeatureCollection',
    features: nextFeatures,
  });
  const geojsonBlob = new Blob([geojsonString], {
    type: 'application/geo+json',
  });
  FileSaver.saveAs(geojsonBlob, filename);
};

export const ExportStep = ({features, onBack, onClose}) => {
  const [filename, setFilename] = useState('');

  // TODO format the counts.
  const [includedCount, excludedCount] = useMemo(() => {
    const included = features.filter(
      f => f.properties._include === true,
    ).length;
    return [included, features.length - included];
  }, [features]);

  return (
    <Box
      sx={{
        flex: 1,
        flexDirection: 'column',
        display: 'flex',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          padding: 1,
        }}
      >
        <Box
          sx={{
            flex: 1,
          }}
        >
          <Button startIcon={<BackIcon />} onClick={() => onBack()}>
            Back to review
          </Button>
        </Box>
        <Box>
          <Button onClick={() => onClose()}>Exit</Button>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: 312,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              padding: 2,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ConfirmBadge />
          </Box>
          <Typography variant="h5">Detects ready for export</Typography>
          <Box
            sx={{
              paddingTop: 4,
            }}
          >
            <Typography variant="overline">Summary</Typography>
          </Box>

          <Grid container spacing={1} sx={{marginTop: 1}}>
            <Grid size={6}>Detects included</Grid>

            <Grid align="right" size={6}>
              {includedCount}
            </Grid>

            <Grid size={6}>Detects excluded</Grid>

            <Grid align="right" size={6}>
              {excludedCount}
            </Grid>
          </Grid>

          <Box
            sx={{
              paddingTop: 4,
            }}
          >
            <Typography variant="overline">
              Name your file for export
            </Typography>
          </Box>
          <Grid container>
            <Grid size={12}>
              <TextField
                variant="outlined"
                placeholder="Filename"
                onChange={evt => setFilename(evt.target.value)}
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">.geojson</InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
            <Grid align="right" size={12}>
              <Button
                disabled={filename.length < 1}
                variant="contained"
                color="primary"
                sx={{marginLeft: 1}}
                onClick={() => {
                  // prompt the user to save the features
                  saveFeatures(filename + '.geojson', features);
                  // close the modal.
                  onClose();
                }}
              >
                Export
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

ExportStep.propTypes = {
  features: PropTypes.array,
  onBack: PropTypes.func,
  onClose: PropTypes.func,
};
